import React from 'react';
import dot from 'dot-object';
import moment from 'moment';
import { Icon } from '@homeexchange/design';
import { parseISODateString } from '@homeexchange/shared';
import Model from './Model';
import Filters from './Filters';
import i18n from '../i18n';
import Api from '../api/Api';

export default class Alert extends Model {
    static DEFAULT_PLACE = 'everywhere';

    static STATUS_DELETED = -1;
    static STATUS_DISABLED = 0;
    static STATUS_ENABLED = 1;

    static DESCRIPTION_MAX_LEN = 200;

    parse(attributes, options) {
        attributes = super.parse(attributes, options);
        if (attributes.hasOwnProperty('params') && _.isObject(attributes.params)) {
            attributes.params.place = _.has(attributes.params, 'place')
                ? attributes.params.place
                : Alert.DEFAULT_PLACE;
        }
        return attributes;
    }

    isOutdated() {
        const alertData = this.attributes || this;
        const dateRanges = alertData.searchCriteria?.calendar?.dateRanges;

        if (dateRanges && dateRanges.length > 0) {
            const today = moment().startOf('day');
            return dateRanges.every((range) => {
                const endDate = moment(parseISODateString(range.to));
                return endDate.isSameOrBefore(today, 'day');
            });
        }

        return false;
    }

    hasDates() {
        const alertData = this.attributes || this;
        const dateRanges = alertData.searchCriteria?.calendar?.dateRanges;

        if (dateRanges && dateRanges.length > 0) {
            return dateRanges.some((range) => range.from && range.to);
        }

        return false;
    }

    static fromSearch(query, filters) {
        filters = new Filters(filters);
        filters = filters.toJSON();
        let data = {
            collection: filters.collection,
            place: query,
            luxury: filters.luxury,
            date_format: 'Y-m-d',
            home_capacity: filters.guests_nb ? String(filters.guests_nb) : null,
            guestpoints_from: filters.guestpoints.from,
            guestpoints_to: filters.guestpoints.to,
            home_type: filters.home.type,
            bedrooms: filters.home.size ? filters.home.size.bedrooms : null,
            bathrooms: filters.home.size ? filters.home.size.bathrooms : null,
            polygon: filters.location.polygon ? filters.location.polygon : null,
            bounds: filters.location.bounds
                ? [
                      filters.location.bounds.sw.lat,
                      filters.location.bounds.sw.lon,
                      filters.location.bounds.ne.lat,
                      filters.location.bounds.ne.lon
                  ].join(',')
                : null,
            country: filters.location.country ? filters.location.country : null,
            filter: _.union(filters.filters, filters.home.amenities),
            place_id: filters.placeId,
            beds:
                filters.home.size && filters.home.size.beds
                    ? {
                          adults: filters.home.size.beds.adults,
                          children: filters.home.size.beds.children,
                          babies: filters.home.size.beds.babies
                      }
                    : null
        };

        if (filters.calendar) {
            data = {
                ...data,
                ...filters.calendar
            };
        }
        if (filters.location.user) {
            const tmp = { user_filters: filters.location.user };
            data = Object.assign({}, data, dot.dot(tmp));
        }
        data = _.omit(
            data,
            (value) => _.isNull(value) || _.isUndefined(value) || (_.isObject(value) && _.isEmpty(value))
        );
        return new Alert({ params: data });
    }

    title() {
        const alertData = this.attributes || this;
        const placeName = alertData.displayInformation?.title || alertData.place;

        return placeName;
    }

    url() {
        const alertData = this.attributes || this;
        const placeName = alertData.displayInformation?.title || alertData.place;

        if (alertData.queryParams) {
            return (
                i18n.t('search:search_url', {
                    slug: encodeURIComponent(placeName || 'everywhere')
                }) + `?${alertData.queryParams}`
            );
        }

        return i18n.t('search:search_url', {
            slug: encodeURIComponent(placeName || 'everywhere')
        });
    }

    save() {
        return Api.Alert.save(this)
            .done((response) => {
                this.set('id', response.id);
                const content = (
                    <div>
                        <p>${i18n.t('search:destination.created.text')}</p>
                        <p>
                            <a href={i18n.t('url:saved_search')}>
                                {i18n.t('search:destination.created.link')}{' '}
                                <Icon name="ChevronRight" size="large" />
                            </a>
                        </p>
                    </div>
                );
                sweetAlert({
                    icon: 'success',
                    title: i18n.t('search:destination.created.title'),
                    content
                });
            })
            .fail(() => {
                sweetAlert(
                    i18n.t('search:alert.create.failed.title'),
                    i18n.t('search:alert.create.failed.text'),
                    'error'
                );
            });
    }

    remove() {
        const deferred = $.Deferred();

        sweetAlert({
            title: i18n.t('search:alert.delete.title'),
            text: i18n.t('search:alert.delete.text'),
            icon: 'warning',
            buttons: {
                cancel: {
                    text: i18n.t('search:alert.delete.cancel'),
                    visible: true
                },
                confirm: {
                    text: i18n.t('search:alert.delete.confirm'),
                    closeModal: false
                }
            }
        }).then((isConfirm) => {
            if (isConfirm) {
                Api.Alert.remove(this)
                    .done(() => {
                        deferred.resolve(this);
                        sweetAlert(
                            i18n.t('search:alert.deleted.title'),
                            i18n.t('search:alert.deleted.text'),
                            'success'
                        );
                    })
                    .fail((error) => {
                        deferred.reject(error);
                        sweetAlert(
                            i18n.t('search:alert.deleted.failed.title'),
                            i18n.t('search:alert.deleted.failed.text'),
                            'error'
                        );
                    });
            } else {
                deferred.reject('USER_CANCEL');
            }
        });

        return deferred;
    }

    toggle() {
        // Optimistic approach (we bet that it's going to succeed)
        const initialStatus = this.get('status');
        const status =
            this.get('status') == Alert.STATUS_DISABLED ? Alert.STATUS_ENABLED : Alert.STATUS_DISABLED;
        this.set('status', status);
        return Api.Alert.patch(this, { status }).fail(() => {
            this.set('status', initialStatus);
        });
    }

    static sortByDate(a, b) {
        const getLatestEndDate = (alert) => {
            const alertData = alert.attributes || alert;
            const dateRanges = alertData.searchCriteria?.calendar?.dateRanges;

            if (dateRanges && dateRanges.length > 0) {
                return dateRanges.reduce((latest, range) => {
                    const endDate = moment(parseISODateString(range.to));
                    return !latest || endDate.isAfter(latest) ? endDate : latest;
                }, null);
            }

            return null;
        };

        const dateA = getLatestEndDate(a);
        const dateB = getLatestEndDate(b);

        if (!dateA && !dateB) {
            if (a.id <= b.id) {
                return 1;
            }
            return -1;
        }
        if (!dateB) {
            return -1;
        }
        if (!dateA) {
            return 1;
        }
        if (dateA.isBefore(dateB)) {
            return -1;
        }
        if (dateA.isSameOrAfter(dateB)) {
            return 1;
        }
        return 0;
    }
}
